import { usePendingSendsV2 } from '@/hooks/useBridge';
import { ugravitonToGrav } from '@/services/bridge-messages';
import type { PendingSendV2 } from '@/services/self-relay';
import type { ReactElement } from 'react';

interface PendingSendsProps {
  gravityAddress: string | undefined;
  onRelayBatch?: (batchNonce: number) => void;
  onRequestBatch?: () => void;
  isRelaying?: boolean;
  relayingBatchNonce?: number | null;
  isRequestingBatch?: boolean;
}

/**
 * Normalize response to ensure arrays exist (handles backend variations)
 */
function normalizePendingSends(data: unknown): { unbatched: PendingSendV2[]; batched: PendingSendV2[] } {
  if (data === null || data === undefined || typeof data !== 'object') {
    return { unbatched: [], batched: [] };
  }
  const obj = data as Record<string, unknown>;
  return {
    unbatched: Array.isArray(obj.unbatched) ? (obj.unbatched as PendingSendV2[]) : [],
    batched: Array.isArray(obj.batched) ? (obj.batched as PendingSendV2[]) : [],
  };
}

/**
 * Props for the inner component that receives guaranteed data
 */
interface PendingSendsContentProps {
  unbatched: PendingSendV2[];
  batched: PendingSendV2[];
  onRelayBatch?: (batchNonce: number) => void;
  onRequestBatch?: () => void;
  isRelaying?: boolean;
  relayingBatchNonce?: number | null;
  isRequestingBatch?: boolean;
}

/**
 * Inner component that renders pending sends - receives validated arrays
 */
function PendingSendsContent({ 
  unbatched, 
  batched, 
  onRelayBatch,
  onRequestBatch,
  isRelaying,
  relayingBatchNonce,
  isRequestingBatch,
}: PendingSendsContentProps): ReactElement | null {
  // No pending sends
  const hasPending = unbatched.length > 0 || batched.length > 0;
  if (hasPending === false) {
    return null;
  }

  // Group batched transfers by batch nonce
  const batchGroups = new Map<number, PendingSendV2[]>();
  for (const send of batched) {
    if (send.batch_info !== null && send.batch_info !== undefined) {
      const nonce = send.batch_info.batch_nonce;
      const existing = batchGroups.get(nonce) ?? [];
      existing.push(send);
      batchGroups.set(nonce, existing);
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4">Pending Transfers to Ethereum</h3>
      
      {/* Batched transfers (in batch, waiting for relay) - grouped by batch */}
      {batchGroups.size > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-400 mb-2">Ready for Relay</h4>
          <div className="space-y-4">
            {Array.from(batchGroups.entries()).map(([batchNonce, sends]) => {
              const isThisBatchRelaying = isRelaying === true && relayingBatchNonce === batchNonce;
              const totalAmount = sends.reduce((acc, send) => {
                const amt = getAmountValue(send);
                return acc + amt;
              }, 0);

              return (
                <div
                  key={`batch-${batchNonce}`}
                  className="bg-gray-700 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">
                        Batch #{batchNonce}
                      </p>
                      <p className="text-sm text-gray-400">
                        {sends.length} transfer{sends.length > 1 ? 's' : ''} • 
                        Total: {(totalAmount / 1_000_000).toFixed(2)} GRAV
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded bg-yellow-600/20 text-yellow-400 text-xs">
                        Batched
                      </span>
                      {onRelayBatch !== undefined && (
                        <button
                          onClick={() => onRelayBatch(batchNonce)}
                          disabled={isRelaying === true}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isThisBatchRelaying
                              ? 'bg-purple-600/50 text-purple-200 cursor-wait'
                              : isRelaying === true
                              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              : 'bg-purple-600 hover:bg-purple-500 text-white'
                          }`}
                        >
                          {isThisBatchRelaying ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                              Relaying...
                            </span>
                          ) : (
                            'Relay to ETH'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Individual transfers in this batch */}
                  <div className="space-y-2 border-t border-gray-600 pt-3">
                    {sends.map((send, index) => (
                      <div
                        key={send.tx_id !== undefined ? `tx-${send.tx_id}` : `batched-${batchNonce}-${index}`}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-gray-300">
                          {getAmountDisplay(send)} GRAV
                        </span>
                        <span className="text-gray-400">
                          → {shortenAddress(send.eth_dest)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unbatched transfers (not yet batched) */}
      {unbatched.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-400">Waiting to be Batched</h4>
            {onRequestBatch !== undefined && (
              <button
                onClick={onRequestBatch}
                disabled={isRequestingBatch === true || isRelaying === true}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isRequestingBatch === true
                    ? 'bg-purple-600/50 text-purple-200 cursor-wait'
                    : isRelaying === true
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-500 text-white'
                }`}
              >
                {isRequestingBatch === true ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    Requesting...
                  </span>
                ) : (
                  'Request Batch'
                )}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {unbatched.map((send, index) => (
              <div
                key={send.tx_id !== undefined ? `tx-${send.tx_id}` : `pending-${index}`}
                className="bg-gray-700 rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium">
                    {getAmountDisplay(send)} GRAV
                  </p>
                  <p className="text-sm text-gray-400">
                    To: {shortenAddress(send.eth_dest)}
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="px-2 py-1 rounded bg-blue-600/20 text-blue-400 text-xs">
                    Pending
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Component to display pending sends to Ethereum
 * Validates data shape at runtime before rendering
 */
export default function PendingSends({ 
  gravityAddress, 
  onRelayBatch,
  onRequestBatch,
  isRelaying,
  relayingBatchNonce,
  isRequestingBatch,
}: PendingSendsProps): ReactElement | null {
  const { data: pendingSends, isLoading, isSuccess, error } = usePendingSendsV2(gravityAddress);

  // Don't render if no address
  if (gravityAddress === undefined || gravityAddress === '') {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Pending Transfers to Ethereum</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error !== null) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Pending Transfers to Ethereum</h3>
        <p className="text-red-400 text-sm">Failed to load pending transfers</p>
      </div>
    );
  }

  // Validate and normalize data at runtime
  if (isSuccess) {
    // Runtime validation - normalize to safe arrays even if backend returns unexpected shape
    const normalized = normalizePendingSends(pendingSends);
    return (
      <PendingSendsContent 
        unbatched={normalized.unbatched} 
        batched={normalized.batched}
        onRelayBatch={onRelayBatch}
        onRequestBatch={onRequestBatch}
        isRelaying={isRelaying}
        relayingBatchNonce={relayingBatchNonce}
        isRequestingBatch={isRequestingBatch}
      />
    );
  }

  // Query not yet enabled or other state - don't render
  return null;
}

/**
 * Shorten an Ethereum address for display
 */
function shortenAddress(address: string | undefined | null): string {
  if (address === undefined || address === null || address === '') {
    return 'Unknown';
  }
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Get numeric amount value from a PendingSendV2 (for aggregation)
 */
function getAmountValue(send: PendingSendV2): number {
  if (send.amount === undefined || send.amount === null) {
    return 0;
  }
  if (typeof send.amount === 'string') {
    return parseInt(send.amount, 10) || 0;
  }
  if (typeof send.amount === 'object' && send.amount.amount !== undefined) {
    return parseInt(send.amount.amount, 10) || 0;
  }
  return 0;
}

/**
 * Safely get amount string from a PendingSendV2
 */
function getAmountDisplay(send: PendingSendV2): string {
  if (send.amount === undefined || send.amount === null) {
    return '0';
  }
  if (typeof send.amount === 'string') {
    return ugravitonToGrav(send.amount);
  }
  if (typeof send.amount === 'object' && send.amount.amount !== undefined) {
    return ugravitonToGrav(send.amount.amount);
  }
  return '0';
}
